import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from '@/utils/tv';
import { Icon } from '@/components/Icons';
import { Close } from '@/components/Icons/Close';
import { AnimationState, IconType, SnackbarVariant } from '@/types/components';

const AUTO_DISMISS_MS = 3000;
const ANIMATION_MS = 250;

const { Success, Error } = SnackbarVariant;
const { Enter, Shown, Exit } = AnimationState;

const snackbar = tv({
  base: 'flex w-full items-center gap-3 rounded-2xl p-4 shadow-modal transform-gpu transition-[opacity,transform] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[opacity,transform]',
  variants: {
    width: {
      default: 'md:w-[400px]',
      compact: 'md:w-auto md:min-w-[300px] md:max-w-[400px]',
    },
    variant: {
      [Success]:
        'bg-successful text-white dark:bg-success dark:text-constant-black',
      [Error]: 'bg-destructive text-white dark:bg-red dark:text-constant-black',
    },
    state: {
      [Enter]: 'opacity-0 -translate-y-4 md:translate-y-4',
      [Shown]: 'opacity-100 translate-y-0',
      [Exit]: 'opacity-0 translate-y-0',
    },
  },
});

const VARIANT_ICONS: Record<SnackbarVariant, IconType> = {
  [Success]: IconType.CheckCircle,
  [Error]: IconType.Alert,
};

interface Props {
  message: string;
  isOpen: boolean;
  variant?: SnackbarVariant;
  compact?: boolean;
  onDismiss: () => void;
}

export const Snackbar: React.FC<Props> = ({
  message,
  isOpen,
  variant = Success,
  compact = false,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState(Enter);
  const onDismissRef = useRef<() => void>(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setAnimationState(Enter);
      const raf = window.requestAnimationFrame(() => setAnimationState(Shown));
      const dismissTimer = window.setTimeout(
        () => onDismissRef.current(),
        AUTO_DISMISS_MS,
      );
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(dismissTimer);
      };
    }

    setAnimationState(Exit);
    const timer = window.setTimeout(() => setShouldRender(false), ANIMATION_MS);

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-[calc(16px+env(safe-area-inset-top))] z-[80] flex justify-center px-4 md:top-auto md:bottom-6 md:px-0"
    >
      <div
        className={snackbar({
          variant,
          state: animationState,
          width: compact ? 'compact' : 'default',
        })}
      >
        <Icon icon={VARIANT_ICONS[variant]} className="size-4 shrink-0" />
        <p className="flex-1 text-subhead-14">{message}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('global.close')}
          className="flex size-4 shrink-0 cursor-pointer items-center justify-center"
        >
          <Close />
        </button>
      </div>
    </div>
  );
};
